class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.progressBar();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  progressBar(){
    const bar = document.querySelector('.free-shipping-box');
    fetch(`${routes.cart_url}.js`)
    .then(response => response.json())
    .then(cart => {

      const percentage = Math.max(
    Math.min(cart.total_price / 100000 * 100, 100),
    cart.total_price >= 100000 ? 100 :
    (cart.total_price >= 98000 ? 99 : 
    (cart.total_price >= 96000 ? 98 : 
    (cart.total_price >= 94000 ? 97 : 
    (cart.total_price >= 92000 ? 96 : 
    (cart.total_price >= 90000 ? 95 : 
    (cart.total_price >= 88000 ? 94 : 
    (cart.total_price >= 86000 ? 94 : 
    (cart.total_price >= 84000 ? 92 : 
    (cart.total_price >= 82000 ? 92 : 
    (cart.total_price >= 80000 ? 90 : 
    (cart.total_price >= 78000 ? 88 : 
    (cart.total_price >= 76000 ? 86 : 
    (cart.total_price >= 75000 ? 85 : 
    (cart.total_price >= 74000 ? 84 : 
    (cart.total_price >= 72000 ? 82 :
    (cart.total_price >= 70000 ? 80 :
    (cart.total_price >= 68000 ? 78 :
    (cart.total_price >= 66000 ? 76 :
    (cart.total_price >= 64000 ? 76 :
    (cart.total_price >= 62000 ? 74 :
    (cart.total_price >= 60000 ? 74 :
    (cart.total_price >= 58000 ? 72 :
    (cart.total_price >= 56000 ? 72 : 
    (cart.total_price >= 54000 ? 70 : 
    (cart.total_price >= 52000 ? 70 : 
    (cart.total_price >= 50000 ? 68 : 
    (cart.total_price >= 49000 ? 67 : 
    (cart.total_price >= 48000 ? 66 : 
    (cart.total_price >= 46000 ? 65: 
    (cart.total_price >= 44000 ? 64 : 
    (cart.total_price >= 42000 ? 64 : 
    (cart.total_price >= 40000 ? 63 : 
    (cart.total_price >= 38000 ? 62 : 
    (cart.total_price >= 36000 ? 60 : 
    (cart.total_price >= 34000 ? 58 : 
    (cart.total_price >= 32000 ? 56 : 
    (cart.total_price >= 30000 ? 56 : 
    (cart.total_price >= 29000 ? 54 : 
    (cart.total_price >= 28000 ? 54 : 
    (cart.total_price >= 27000 ? 52 : 
    (cart.total_price >= 26000 ? 52 : 
    (cart.total_price >= 25000 ? 50 : 
    (cart.total_price >= 24000 ? 48 : 
    (cart.total_price >= 23000 ? 46 : 
    (cart.total_price >= 22000 ? 44 : 
    (cart.total_price >= 21000 ? 42 : 
    (cart.total_price >= 20000 ? 40 : 
    (cart.total_price >= 19000 ? 39 :
    (cart.total_price >= 18000 ? 38 : 
    (cart.total_price >= 17000 ? 38 :
    (cart.total_price >= 16000 ? 37 :
    (cart.total_price >= 15000 ? 34 :
    (cart.total_price >= 14000 ? 33 :
    (cart.total_price >= 13000 ? 32 :
    (cart.total_price >= 12000 ? 31 :
    (cart.total_price >= 11000 ? 30 :
    (cart.total_price >= 10000 ? 28 : 
    (cart.total_price >= 9900 ? 27 :
    (cart.total_price >= 9000 ? 25 :
    (cart.total_price >= 8000 ? 23 :
    (cart.total_price >= 7000 ? 22 :
    (cart.total_price >= 6000 ? 20 :
    (cart.total_price >= 5000 ? 18 :
    (cart.total_price >= 4000 ? 16 : 
    (cart.total_price >= 3000 ? 14 :
    (cart.total_price >= 2000 ? 12 :
    (cart.total_price >= 1000 ? 10 :
    (cart.total_price >= 500 ? 9 : 0))))))))))))))))))))))))))))))))))))))))))))))))))))))))))))))))))))
);

      const breakpoint_1 = document.getElementById('breakpoint-1');
      const breakpoint_2 = document.getElementById('breakpoint-2');
      const breakpoint_3 = document.getElementById('breakpoint-3');

      const bp_tier_1 = breakpoint_1.dataset.treshold; 
      const bp_tier_2 = breakpoint_2.dataset.treshold;
      const bp_tier_3 = breakpoint_3.dataset.treshold;

      const bp_tier_2_free_gift = breakpoint_2.dataset.id;
      const bp_tier_3_free_gift = breakpoint_3.dataset.id;

      //console.log(bp_tier_2_free_gift + ' - ' +  bp_tier_3_free_gift);

      const progressBar = document.getElementById('progressBar');

      if(percentage >= bp_tier_1){
        progressBar.style.width = percentage + '%';
        breakpoint_1.classList.add('active');
      }
      else{
        progressBar.style.width = percentage + '%';
        breakpoint_1.classList.remove('active');
      }

      const anchor1 = document.querySelectorAll(`a[data-handle="${bp_tier_2_free_gift}"]`);
      const anchor2 = document.querySelectorAll(`a[data-handle="${bp_tier_3_free_gift}"]`);
      const removeButton = document.querySelectorAll('cart-remove-button');
      const cartCount = document.querySelectorAll('#drawer__heading-count') || document.querySelector('.cart-count-bubble span');

      if(percentage >= bp_tier_3){
        breakpoint_2.classList.add('active');
        breakpoint_3.classList.add('active');
        const parentElement = document.getElementById('cartTable');

        if(anchor2.length < 1 && anchor1.length < 1){
          this.generateHTML('freeGift3');

          removeButton.forEach((element) => {
            let currentIndex = parseInt(element.dataset.index);
            console.log(currentIndex);
            let newIndex = currentIndex + 1;
            element.dataset.index = newIndex; // Set the 'data-index' attribute to the current index
          });
          
          const giftData = [{ "id": bp_tier_2_free_gift, "quantity":0}, {"id": bp_tier_3_free_gift, "quantity":1}];
          const giftObject = {};
          for (var i = 0; i < giftData.length; i++) {
            var key = giftData[i].id;
            giftObject[key] = giftData[i].quantity;
          }
          this.updateItemCart(giftObject);

        }
        else if(anchor1.length >= 1 && anchor2.length < 1){
          this.generateHTML('freeGift3');

          removeButton.forEach((element) => {
            let currentIndex = parseInt(element.dataset.index);
            let newIndex = currentIndex + 1;
            element.dataset.index = newIndex; // Set the 'data-index' attribute to the current index
          });

          for (var i = 0; i < anchor1.length; i++) {
            const freeGiftitem_1 = anchor1[i].closest('tr');
            parentElement.removeChild(freeGiftitem_1);
          }

          const giftData = [{ "id": bp_tier_2_free_gift, "quantity":0}, {"id": bp_tier_3_free_gift, "quantity":1}];
          const giftObject = {};
          for (var i = 0; i < giftData.length; i++) {
            var key = giftData[i].id;
            giftObject[key] = giftData[i].quantity;
          }
          this.updateItemCart(giftObject);
        }
      }
      else if(percentage >= bp_tier_2){
        breakpoint_2.classList.add('active');
        const parentElement = document.getElementById('cartTable');

        if(anchor1.length < 1 ){
          this.generateHTML('freeGift2');
          removeButton.forEach((element) => {
            let currentIndex = parseInt(element.dataset.index);
            let newIndex = currentIndex + 1;
            element.dataset.index = newIndex; // Set the 'data-index' attribute to the current index
          });
        }

        if(anchor2.length > 0 ){
          for (var i = 0; i < anchor2.length; i++) {
            const freeGiftitem_2 = anchor2[i].closest('tr');
            parentElement.removeChild(freeGiftitem_2);
          }
          removeButton.forEach((element) => {
            let currentIndex = parseInt(element.dataset.index);
            let newIndex = currentIndex - 1;
            element.dataset.index = newIndex; // Set the 'data-index' attribute to the current index
          });
        }
        

        const giftData = [{ "id": bp_tier_2_free_gift, "quantity":1,}, {"id": bp_tier_3_free_gift, "quantity":0}];
        const giftObject = {};
        for (var i = 0; i < giftData.length; i++) {
          var key = giftData[i].id;
          giftObject[key] = giftData[i].quantity;
        }
        this.updateItemCart(giftObject);
      }
      else{
        breakpoint_2.classList.remove('active');
        breakpoint_3.classList.remove('active');
        const parentElement = document.getElementById('cartTable');

        if(anchor1.length >= 1){
          removeButton.forEach((element) => {
            let currentIndex = parseInt(element.dataset.index);
            let newIndex = currentIndex - 1;
            element.dataset.index = newIndex; // Set the 'data-index' attribute to the current index
          });
        }
        if(anchor1.length > 0 || anchor2.length > 0){

          for (var i = 0; i < anchor1.length; i++) {
            const freeGiftitem_1 = anchor1[i].closest('tr');
            parentElement.removeChild(freeGiftitem_1);
          }
          
          for (var i = 0; i < anchor2.length; i++) {
            const freeGiftitem_2 = anchor2[i].closest('tr');
            parentElement.removeChild(freeGiftitem_2);
          }
          
          const giftData = [{ "id": bp_tier_2_free_gift, "quantity":0}, {"id": bp_tier_3_free_gift, "quantity":0}];
          const giftObject = {};
          for (var i = 0; i < giftData.length; i++) {
            var key = giftData[i].id;
            giftObject[key] = giftData[i].quantity;
          }
          this.updateItemCart(giftObject);
        }
      }

      progressBar.style.width = percentage + '%';

    })
    .catch(error => {
      console.error('Error:', error);
    });
  }

  generateHTML(gift) {
    const scriptElement = document.getElementById(gift);
    const cartItems = document.getElementById('cartTable');

    const templateContent = scriptElement.innerHTML;
    const newElement = document.createElement('tr');
    newElement.setAttribute('id', 'CartDrawer-Free-Item');
    newElement.setAttribute('class', 'cart-item');
    newElement.innerHTML = templateContent;

    var parentElement = cartItems.parentNode;

    parentElement.insertBefore(newElement, cartItems);
  }
  
  updateItemCart(product_data) {
    const productData = product_data;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates: productData })
    })
    .then(function(response) {
      return response.json();
    })
    .then(function(data) {
      //console.log('Cart updated:', data);
    })
    .catch(function(error) {
      console.error('Error updating cart:', error);
    });
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

